import {createContext, useContext, useMemo, useRef, useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {useEvidenciaPintada, usePulsoDeEspera} from '@/components/bateria/cola/evidencia-de-la-tarjeta'
import {marcarRecorteCaido} from '@/lib/recortes-caidos'
import {cn} from '@/lib/utils'
import type {TarjetaCola} from '@/lib/bateria-cola'

/**
 * Un `<img>` reapuntado conserva el frame viejo hasta decodificar el nuevo: cada vecina tiene su propio `<img>`
 * con URL fija. La ventana (±8/±3) es un subconjunto de la de precarga (±16/±4), que ya calentó el caché.
 */
const ADELANTE = 8
const ATRAS = 3

type ItemVentana = {ref: string; url: string}

const PilaDeRecortesContext = createContext<ItemVentana[] | null>(null)

/** Por ref y no por URL: la ref es la `key` de React. */
export function ventanaDeVecinas(
  tarjetas: readonly TarjetaCola[],
  indice: number,
  adelante: number = ADELANTE,
  atras: number = ATRAS
): ItemVentana[] {
  const items: ItemVentana[] = []
  const vistas = new Set<string>()
  for (let salto = -atras; salto <= adelante; salto += 1) {
    const tarjeta = tarjetas[indice + salto]
    if (!tarjeta?.crop_download_url || vistas.has(tarjeta.ref)) continue
    vistas.add(tarjeta.ref)
    items.push({ref: tarjeta.ref, url: tarjeta.crop_download_url})
  }
  return items
}

/**
 * Se monta una vez sobre las cuatro tarjetas: cambiar de tipo no reinicia la ventana. Sin proveedor,
 * cada `PilaDeRecortes` es su propia pila de un elemento.
 */
export function PilaDeRecortesProvider({
  tarjetas,
  indice,
  children,
}: {
  tarjetas: readonly TarjetaCola[]
  indice: number
  children: ReactNode
}) {
  const ventana = useMemo(() => ventanaDeVecinas(tarjetas, indice), [tarjetas, indice])
  return <PilaDeRecortesContext.Provider value={ventana}>{children}</PilaDeRecortesContext.Provider>
}

type PilaProps = {
  url: string
  alt: string
  fallback: string
  testId?: string
  /** Opt-in: `TiraOpciones` ya arma su marco y duplicarlo dejaría doble borde. */
  marco?: boolean
  /** Sin efecto sin `marco`. */
  pie?: string
}

/** Nunca `display:none` ni desmontar una vecina en la ventana: se perdería lo decodificado; se alterna por opacidad. */
export function PilaDeRecortes({url, alt, fallback, testId = 'tira-opciones-crop', marco = false, pie}: PilaProps) {
  const {t} = useTranslation()
  const ventana = useContext(PilaDeRecortesContext)

  const items = useMemo<ItemVentana[]>(() => {
    if (!ventana || ventana.length === 0) return [{ref: url, url}]
    if (ventana.some((item) => item.url === url)) return ventana
    // Salto largo fuera de la ventana: la tarjeta en pantalla siempre tiene su elemento.
    return [{ref: url, url}, ...ventana]
  }, [ventana, url])

  const [listas, setListas] = useState<ReadonlySet<string>>(() => new Set())
  const [fallidas, setFallidas] = useState<ReadonlySet<string>>(() => new Set())

  const marcarLista = (u: string) => setListas((previas) => (previas.has(u) ? previas : new Set(previas).add(u)))
  const marcarFallida = (u: string) => setFallidas((previas) => (previas.has(u) ? previas : new Set(previas).add(u)))

  // Antes de cualquier salida temprana (reglas de hooks); una imagen fallida cuenta como pintada.
  const lista = listas.has(url)
  const fallida = fallidas.has(url)
  useEvidenciaPintada(url, lista || fallida)
  const pulso = usePulsoDeEspera()

  // Sin URL cae al mismo aviso que un recorte fallido: nunca un `<img src="">`, que no dispara `onError`.
  if (!url || fallida) {
    const aviso = (
      <div className="flex h-24 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
        {fallback}
      </div>
    )
    return marco ? <Marco pie={pie}>{aviso}</Marco> : aviso
  }

  const pila = (
    <div className="relative block max-h-40 min-h-40 w-full">
      {items.map((item) => (
        <ImagenDeVecina
          key={item.ref}
          url={item.url}
          alt={alt}
          esActual={item.url === url}
          listo={listas.has(item.url)}
          testId={testId}
          onListo={() => marcarLista(item.url)}
          onFalla={() => marcarFallida(item.url)}
        />
      ))}
      {/* Nunca la imagen de otra tarjeta, ni un frame: mientras la actual no está lista, solo se ve esto. */}
      {!lista && (
        <div
          // El `key` reinicia la animación en cada intento; sin él la segunda no se vería.
          key={pulso}
          className={cn(
            'pointer-events-none absolute inset-0 z-10 flex items-center justify-center gap-1.5',
            pulso > 0 && 'pulso-espera'
          )}
          data-testid={`${testId}-cargando`}>
          <Skeleton className="absolute inset-0 rounded-md" />
          <span className="relative font-mono text-[11px] text-muted-foreground">
            {t('admin.bateria.tiraOpciones.cargandoRecorte')}
          </span>
        </div>
      )}
    </div>
  )

  return marco ? <Marco pie={pie}>{pila}</Marco> : pila
}

/** `bg-muted` bajo la imagen: una página en blanco se confundiría con la tarjeta blanca. */
function Marco({pie, children}: {pie?: string; children: ReactNode}) {
  return (
    <figure className="space-y-1.5">
      {pie && (
        <figcaption className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">{pie}</figcaption>
      )}
      <div className="rounded-md border border-border bg-muted p-3">{children}</div>
    </figure>
  )
}

/** Decodifica apenas monta y se muestra solo cuando es la actual y ya terminó. */
function ImagenDeVecina({
  url,
  alt,
  esActual,
  listo,
  testId,
  onListo,
  onFalla,
}: {
  url: string
  alt: string
  esActual: boolean
  listo: boolean
  testId: string
  onListo: () => void
  onFalla: () => void
}) {
  const avisada = useRef(false)
  const mostrar = esActual && listo

  return (
    <img
      src={url}
      alt={mostrar ? alt : ''}
      decoding="async"
      aria-hidden={!mostrar}
      onLoad={(e) => {
        const elemento = e.currentTarget
        const marcar = () => {
          if (avisada.current) return
          avisada.current = true
          onListo()
        }
        const decodificada = typeof elemento.decode === 'function' ? elemento.decode() : Promise.resolve()
        decodificada.then(marcar, marcar)
      }}
      onError={() => {
        if (avisada.current) return
        avisada.current = true
        // Antes de `onFalla`: en el mismo render en que esta imagen falla, la tarjeta ya mira el peldaño siguiente.
        marcarRecorteCaido(url)
        onFalla()
      }}
      className={cn(
        'absolute inset-0 block h-full w-full rounded-md object-contain',
        mostrar ? 'visible opacity-100' : 'invisible opacity-0'
      )}
      data-testid={esActual ? testId : undefined}
    />
  )
}
